<template>
  <div class="membership-panel" :aria-busy="loading">
    <div v-if="loading" class="membership-skeleton" aria-hidden="true">
      <span class="skeleton-line"></span>
      <span class="skeleton-track"></span>
    </div>
    <template v-else>
      <div class="usage-row">
        <span class="plan-name">{{ planName }}</span>
        <span class="usage-value">
          <strong>{{ monthlyRemainingUsage }}</strong>
          <small>/ {{ currentMaxUsage }}</small>
        </span>
      </div>
      <div class="usage-track" :class="`is-${usageStatus}`" :title="$t('userMenu.monthlyBalance')">
        <span :style="{ width: `${usagePercentage}%` }"></span>
      </div>
      <template v-if="showPurchasedCredits">
        <div class="usage-row permanent-row">
          <span class="plan-name">{{ $t('userMenu.permanentBalance') }}</span>
          <span class="usage-value">
            <strong>{{ purchasedCredits }}</strong>
            <small>/ {{ purchasedCreditsTotal }}</small>
          </span>
        </div>
        <div class="usage-track permanent-track" :title="$t('userMenu.permanentBalance')">
          <span :style="{ width: `${purchasedPercentage}%` }"></span>
        </div>
      </template>
      <div v-if="!isPremium && !isPro" class="usage-meta">
        <button v-if="!isPremium && !isPro" type="button" class="upgrade-link" @click="handleUpgrade">
          {{ $t('recentFiles.upgrade') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'MembershipStatus',
  emits: ['upgrade'],
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    isPremium: {
      type: Boolean,
      default: false
    },
    isPro: {
      type: Boolean,
      default: false
    },
    remainingUsage: {
      type: Number,
      default: 0
    },
    maxMonthlyUsage: {
      type: Number,
      default: 200
    },
    monthlyRemainingUsage: {
      type: Number,
      default: 0
    },
    purchasedCredits: {
      type: Number,
      default: 0
    },
    purchasedCreditsTotal: {
      type: Number,
      default: 0
    }
  },
  computed: {
    planName() {
      return this.isPremium
        ? this.$t('recentFiles.premiumUser')
        : this.isPro
          ? this.$t('recentFiles.proUser')
          : this.$t('recentFiles.freeUser')
    },
    usagePercentage() {
      if (!this.currentMaxUsage) return 0
      return Math.min((this.monthlyRemainingUsage / this.currentMaxUsage) * 100, 100)
    },
    purchasedPercentage() {
      if (!this.purchasedCreditsTotal) return 0
      return Math.min((this.purchasedCredits / this.purchasedCreditsTotal) * 100, 100)
    },
    showPurchasedCredits() {
      return this.purchasedCreditsTotal > 0 || this.purchasedCredits > 0
    },
    usageStatus() {
      if (this.isPremium || this.isPro) return 'success'
      const percentage = this.usagePercentage
      if (percentage <= 20) return 'exception'
      if (percentage <= 50) return 'warning'
      return 'success'
    },
    currentMaxUsage() {
      return this.maxMonthlyUsage
    }
  },
  methods: {
    handleUpgrade() {
      this.$emit('upgrade')
    }
  }
})
</script>

<style scoped>
.membership-panel {
  display: grid;
  gap: var(--space-2);
  width: 100%;
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
}

.usage-row,
.usage-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
}

.plan-name {
  color: var(--ink-secondary);
  font-weight: 600;
}

.usage-value {
  font-family: var(--font-family-data);
  font-variant-numeric: tabular-nums;
}

.usage-value strong {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.usage-value small {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.usage-track {
  height: 4px;
  overflow: hidden;
  border-radius: var(--radius-pill);
  background: var(--border-subtle);
}

.usage-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--signal-active-strong);
}

.usage-track.is-warning span {
  background: var(--color-warning);
}

.usage-track.is-exception span {
  background: var(--color-danger);
}

.permanent-row {
  margin-top: var(--space-1);
}

.permanent-track span {
  background: var(--signal-marker);
}

.usage-meta {
  color: var(--ink-tertiary);
}

.upgrade-link {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--signal-active-strong);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.upgrade-link:hover {
  text-decoration: underline;
}

.upgrade-link:focus-visible {
  outline: none;
  border-radius: var(--radius-sm);
  box-shadow: var(--focus-ring-brand-strong);
}

.membership-skeleton {
  display: grid;
  gap: var(--space-2);
}

.membership-skeleton span {
  display: block;
  border-radius: var(--radius-pill);
  background: var(--border-subtle);
}

.skeleton-line {
  width: 70%;
  height: 12px;
}

.skeleton-track {
  width: 100%;
  height: 4px;
}
</style>
